import { z } from 'zod';

import { marketTypes } from '~/apps/product-plus/search-domain/config/market-meta';
import {
  type BettingSplitsBet,
  type BettingSplitsESResult,
  type BettingSplitsMarket,
} from '~/apps/product-plus/search-domain/pipelines/betting-splits/types';
import {
  type MatchupCentreESResult,
  type MatchupCentreMarket,
} from '~/apps/product-plus/search-domain/pipelines/match-centre/types';

import { schemaTeam } from './extractSubevent';

function trimString(value: unknown) {
  return typeof value === 'string' ? value.trim() : value;
}

const schemaMarket = z.object({
  betTypeId: z.number(),
  categoryGroupName: z.string(),
  categoryName: z.string(),
  marketClassification: z.enum(marketTypes),
  marketDisplayName: z.string(),
  marketDisplayNameMap: z.object({
    it: z.preprocess(trimString, z.string()).optional(),
    uk: z.preprocess(trimString, z.string()),
    us: z.preprocess(trimString, z.string()),
  }),
  marketGenericSequence: z.string(),
  marketId: z.number(),
  marketName: z.string(),
  marketPropType: z.string(),
  subeventName: z.string(),
  teamData: z.object({
    away: schemaTeam,
    home: schemaTeam,
  }),
});

const schemaMatchupCentreMarket = schemaMarket.omit({ teamData: true }).extend({
  teamData: z
    .object({
      away: schemaTeam,
      home: schemaTeam,
    })
    .nullable(),
});

export function extractMarket(result: BettingSplitsESResult): BettingSplitsMarket {
  const parsedMarket = schemaMarket.safeParse(result);

  if (!parsedMarket.success) {
    throw new Error(JSON.stringify(parsedMarket.error.flatten().fieldErrors));
  }

  const {
    betTypeId,
    categoryGroupName,
    categoryName,
    marketClassification,
    marketDisplayName,
    marketDisplayNameMap,
    marketGenericSequence,
    marketId,
    marketName,
    marketPropType,
    subeventName,
    teamData,
  } = parsedMarket.data;

  return {
    betTypeId,
    bets: [] as BettingSplitsBet[],
    categoryGroupName,
    categoryName,
    marketClassification,
    marketDisplayName,
    marketDisplayNameMap,
    marketGenericSequence,
    marketId,
    marketName,
    marketPropType,
    subeventName,
    teamData: teamData as any,
  };
}

export function extractMatchupCentreMarket(result: MatchupCentreESResult): MatchupCentreMarket {
  const parsedMarket = schemaMatchupCentreMarket.safeParse(result);

  if (!parsedMarket.success) {
    throw new Error(JSON.stringify(parsedMarket.error.flatten().fieldErrors));
  }

  const {
    betTypeId,
    categoryGroupName,
    categoryName,
    marketClassification,
    marketDisplayName,
    marketDisplayNameMap,
    marketGenericSequence,
    marketId,
    marketName,
    marketPropType,
    subeventName,
    teamData,
  } = parsedMarket.data;

  return {
    betTypeId,
    bets: [],
    categoryGroupName,
    categoryName,
    marketClassification,
    marketDisplayName,
    marketDisplayNameMap,
    marketGenericSequence,
    marketId,
    marketName,
    marketPropType,
    subeventName,
    teamData: teamData as any,
  };
}
